import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/lib/v2/components';

import { useService } from '@/src/infrastructure/Protocol/useEmblue';

export const useLegalBillingPdf = () => {
  const [downloadingSerialNumbers, setDownloadingSerialNumbers] = useState<Set<number>>(new Set());
  const service = useService();
  const { t } = useTranslation();

  const downloadPdf = async (serialNumber: number) => {
    if (!service) return;

    try {
      setDownloadingSerialNumbers((prev) => new Set(prev).add(serialNumber));
      const response = await service.getLegalBillingPdf(serialNumber);

      // Create blob from base64
      const byteCharacters = atob(response.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.fileName);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: t('MY_PLAN.LEGAL_BILLING.DOWNLOAD.errorTitle'),
        body: t('MY_PLAN.LEGAL_BILLING.DOWNLOAD.errorBody'),
        variant: 'error',
      });
    } finally {
      setDownloadingSerialNumbers((prev) => {
        const next = new Set(prev);
        next.delete(serialNumber);
        return next;
      });
    }
  };

  return {
    downloadPdf,
    downloadingSerialNumbers,
  };
};
